import React, { FunctionComponent, useContext, useState } from "react";
import { Auth } from "aws-amplify";
import AppUI from "mson-react/lib/app";
import CssBaseline from "@material-ui/core/CssBaseline";
import { MuiThemeProvider } from "@material-ui/core/styles";
import { BrowserRouter, useHistory } from "react-router-dom";
import globals from "mson/lib/globals";
import theme from "../theme";
import {
  Authenticator,
  ForgotPassword,
  RequireNewPassword,
  SignIn,
  VerifyContact,
  Greetings,
} from "aws-amplify-react";

import CustomSignIn from "../components/CustomSignIn";
import CustomForgotPassword from "../components/CustomForgotPassword";
import CustomRequireNewPassword from "../components/CustomRequireNewPassword";
import CustomVerifyContact from "../components/CustomVerifyContact";
import {
  AuthContext,
  AuthProvider,
  CurrentFormContext,
  CurrentFormProvider,
} from "../contexts";
import Buttons from "../lib/Buttons";
import Logs from "../lib/Logs";
import {
  NotificationManager,
  NotificationContainer,
} from "react-notifications";
import {
  AppBar,
  IconButton,
  Toolbar,
  Menu,
  MenuItem,
  makeStyles,
  Theme,
  createStyles,
  Paper,
  Typography,
} from "@material-ui/core";
import { AccountCircle } from "@material-ui/icons";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
    },
    appBar: {
      zIndex: theme.zIndex.drawer + 1,
      transition: theme.transitions.create(["width", "margin"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    },
    menuButton: {
      marginRight: 12,
    },
    hide: {
      display: "none",
    },
    toolbar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      padding: theme.spacing(0, 1),
      // necessary for content to be below app bar
      ...theme.mixins.toolbar,
    },
    content: {
      flexGrow: 1,
      padding: theme.spacing(3),
    },
    paper: {
      margin: theme.spacing(3),
      marginTop: 96,
    },
    title: {
      flexGrow: 1,
    },
    main: {
      marginTop: 32,
      position: "relative",
    },
  })
);

// Note: BrowserRouter needs to be outside of App so that we can use withRouter

interface AppContainerProps {
  basename: string | undefined;
  component: unknown;
  authState: string;
}

const AppContainer: FunctionComponent<AppContainerProps> = ({
  basename,
  component,
  authState,
}) => {
  return (
    <AuthProvider>
      <CurrentFormProvider>
        <MainUI
          component={component}
          authState={authState}
          basename={basename}
        />
      </CurrentFormProvider>
    </AuthProvider>
  );
};

const MainUI: FunctionComponent<AppContainerProps> = ({
  basename,
  component,
  authState,
}) => {
  const classes = useStyles();
  const onNavigate = (message: string, callback: (ok: boolean) => void) => {
    globals.onNavigate(message, callback);
  };
  const { user } = useContext(AuthContext);

  return user ? (
    <BrowserRouter getUserConfirmation={onNavigate} basename={basename}>
      <ToolbarWrapper>
        <AgencyWrapper className={classes.main}>
          <NotificationContainer />;
          <AppUI component={component} className={classes.main} />
          <Buttons
            fail={(message) => NotificationManager.error(message, "", 10000)}
          />
          <Logs />
        </AgencyWrapper>
      </ToolbarWrapper>
    </BrowserRouter>
  ) : null;
};

interface AgencyWrapperProps {
  className: string;
}

const AgencyWrapper: FunctionComponent<AgencyWrapperProps> = ({ children }) => {
  const { agency } = useContext(AuthContext);
  const classes = useStyles();

  if (agency === "Health" || agency === "CQA") {
    return <>{children}</>;
  } else {
    return (
      <Paper className={`${classes.content} ${classes.paper}`}>
        <Typography>You don't have access to this application</Typography>
      </Paper>
    );
  }
};

const ToolbarWrapper: FunctionComponent = ({ children }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { setForm } = useContext(CurrentFormContext);
  const { username } = useContext(AuthContext);
  const history = useHistory();
  const avatarMenuOpen = Boolean(anchorEl);

  const handleMenu = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = () => {
    setForm(undefined);
    history.push("/");
    Auth.signOut().then(() => window.location.reload());
  };

  return (
    <>
      <div
        style={{
          position: "fixed",
          top: "2%",
          right: "45%",
          zIndex: 1202,
          color: "white",
        }}
      >
        <Typography variant="h6">Agent: {username}</Typography>
      </div>
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          <div className={classes.menuButton}>
            <IconButton
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={avatarMenuOpen}
              onClose={handleClose}
            >
              {/* <MenuItem onClick={handleClose}>Profile</MenuItem> */}
              <MenuItem onClick={handleSignOut}>Sign Out</MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>
      <main className={classes.main}>{children}</main>
    </>
  );
};

const Authentication = ({ amplifyConfig, children }) => {
  return (
    <Authenticator
      hide={[
        SignIn,
        ForgotPassword,
        RequireNewPassword,
        VerifyContact,
        Greetings,
      ]}
      amplifyConfig={amplifyConfig}
    >
      <CustomSignIn />
      <CustomForgotPassword />
      <CustomRequireNewPassword />
      <CustomVerifyContact />
      {children}
    </Authenticator>
  );
};

const ThemeWrapper = ({ children }) => (
  <MuiThemeProvider theme={theme}>
    <CssBaseline />
    {children}
  </MuiThemeProvider>
);

const BootstrapApp = ({ amplifyConfig, component }) => {
  return (
    <ThemeWrapper>
      <Authentication amplifyConfig={amplifyConfig}>
        <AppContainer component={component} />
      </Authentication>
    </ThemeWrapper>
  );
};

export default BootstrapApp;
